// src/components/AuthContext.js
import React, { createContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

import {
  clearAuthStorage,
  getAuthToken,
  getStoredFamilyMembers,
  getStoredSelectedMemberId,
  persistSessionMetadata,
  setAuthToken,
  setStoredSelectedMemberId,
} from "../utils/authStorage";

export const AuthContext = createContext();

const defaultAuthState = {
  isAuthenticated: false,
  username: null,
  displayName: null,
  role: null,
  branch: null,
  principalType: null,
  familyId: null,
  membershipId: null,
  memberId: null,
  selectedMemberId: null,
  familyMembers: [],
};

const buildAuthState = (token, sessionData = {}) => {
  try {
    const decodedToken = jwtDecode(token);
    const familyMembers = sessionData.family_members || sessionData.familyMembers || getStoredFamilyMembers();
    const selectedMemberId =
      sessionData.selected_member_id ||
      sessionData.selectedMemberId ||
      getStoredSelectedMemberId() ||
      decodedToken.selected_member_id ||
      decodedToken.member_id ||
      null;

    return {
      isAuthenticated: true,
      username: decodedToken.username || decodedToken.sub,
      displayName: decodedToken.display_name || decodedToken.username || decodedToken.sub,
      role: decodedToken.role,
      branch: decodedToken.branch_id,
      principalType: decodedToken.principal_type || null,
      familyId: decodedToken.family_id || null,
      membershipId: decodedToken.membership_id || null,
      memberId: decodedToken.member_id || null,
      selectedMemberId,
      familyMembers: Array.isArray(familyMembers) ? familyMembers : [],
    };
  } catch (error) {
    console.error("Invalid token:", error);
    return defaultAuthState;
  }
};

const getInitialAuthState = () => {
  const token = getAuthToken();
  return token ? buildAuthState(token) : defaultAuthState;
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(getInitialAuthState);

  const updateAuth = (token, sessionData = {}) => {
    if (!token) {
      clearAuthStorage();
      setAuth(defaultAuthState);
      return;
    }

    setAuthToken(token);
    persistSessionMetadata(sessionData);
    setAuth(buildAuthState(token, sessionData));
  };

  const selectFamilyMember = (memberId) => {
    setStoredSelectedMemberId(memberId);
    setAuth((currentAuth) => ({
      ...currentAuth,
      selectedMemberId: memberId ? String(memberId) : null,
    }));
  };

  const logoutAuth = () => {
    clearAuthStorage();
    setAuth(defaultAuthState);
  };

  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      setAuth(buildAuthState(token));
    }

    const interceptorId = axios.interceptors.request.use((requestConfig) => {
      const currentToken = getAuthToken();
      if (currentToken) {
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers.Authorization = `Bearer ${currentToken}`;
      }
      return requestConfig;
    });

    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);

  const activeMember = useMemo(() => {
    if (!Array.isArray(auth.familyMembers) || auth.familyMembers.length === 0) {
      return null;
    }

    return (
      auth.familyMembers.find(
        (member) => String(member.member_id) === String(auth.selectedMemberId)
      ) || auth.familyMembers[0]
    );
  }, [auth.familyMembers, auth.selectedMemberId]);

  return (
    <AuthContext.Provider
      value={{ ...auth, activeMember, updateAuth, selectFamilyMember, logoutAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};